import React from 'react';
import Button from '../ui/Button';
import { answerLabel, type EventAnswers, type RsvpAnswer } from '../../utils/bulkRsvp';
import type { ConflictGuest } from '../../services/GuestRsvpBulkService';
import type { CardEvent } from './BulkRsvpCard';

export type Resolution = 'mine' | 'theirs';
/** A conflict as the screen holds it: the server's current state of the guest, and what the user had changed. */
export interface OpenConflict { theirs: ConflictGuest; name: string; mine: Record<string, RsvpAnswer> }

interface Props {
  conflicts: OpenConflict[];
  resolutions: Record<string, Resolution | undefined>;
  events: CardEvent[];
  saving: boolean;
  /** Guests in conflict in all; more than are listed when the API cut the list. */
  total: number;
  onResolve: (guestId: string, choice: Resolution) => void;
  onSaveAgain: () => void;
}

const line = (events: CardEvent[], answers: EventAnswers | Record<string, RsvpAnswer>, only?: string[]) =>
  events.filter(e => (only ? only.includes(e.id) : true)).map(e => `${e.title}: ${answerLabel(answers[e.id] ?? null)}`);

/**
 * Review list shown when the server refused the save because guests changed after they were loaded. Only those guests
 * are listed. Each is resolved by the user: keep my changes (retry with the fresh time) or take theirs (drop my edits).
 * Nothing is overwritten until the user chooses.
 */
const BulkRsvpConflicts: React.FC<Props> = ({ conflicts, resolutions, events, saving, total, onResolve, onSaveAgain }) => {
  const allResolved = conflicts.every(c => resolutions[c.theirs.guestId]);
  return (
    <section className="rsvp-conflicts" role="alert" aria-label="Guests that changed while you were editing">
      <h2 className="rsvp-conflicts__title">{conflicts.length === 1 ? '1 guest changed' : `${conflicts.length} guests changed`} while you were editing</h2>
      <p className="rsvp-conflicts__lead">Nothing was saved for this batch. Choose what to do for each guest below. Everyone else keeps their edits.{total > conflicts.length && ` Showing ${conflicts.length} of ${total}; the rest appear if you save again.`}</p>
      <ul className="rsvp-conflicts__list">
        {conflicts.map(c => {
          const id = c.theirs.guestId;
          const picked = resolutions[id];
          const theirs = c.theirs.events;
          return (
            <li key={id} className="rsvp-conflict">
              <strong className="rsvp-conflict__name">{c.name}</strong>
              <div className="rsvp-conflict__cols">
                <div className="rsvp-conflict__theirs">
                  <span className="rsvp-conflict__label">Their current answers</span>
                  {theirs ? <ul>{line(events, theirs).map(t => <li key={t}>{t}</li>)}</ul> : <p>Not shown by the server. Take theirs to reload this guest.</p>}
                </div>
                <div className="rsvp-conflict__mine">
                  <span className="rsvp-conflict__label">Your changes</span>
                  <ul>{line(events, c.mine, Object.keys(c.mine)).map(t => <li key={t}>{t}</li>)}</ul>
                </div>
              </div>
              <div className="rsvp-conflict__choices">
                <Button size="sm" variant="outline" aria-pressed={picked === 'mine'} onClick={() => onResolve(id, 'mine')}>Keep mine</Button>
                <Button size="sm" variant="outline" aria-pressed={picked === 'theirs'} onClick={() => onResolve(id, 'theirs')}>Take theirs</Button>
              </div>
            </li>
          );
        })}
      </ul>
      <Button variant="primary" disabled={!allResolved} loading={saving} onClick={onSaveAgain}>Save again</Button>
    </section>
  );
};

export default BulkRsvpConflicts;
